import React, { Fragment, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FiChevronDown, FiHeart, FiSliders, FiX } from "react-icons/fi";
import "../css/shop.scss";
import productData, { formatPrice } from "../Datas/productData";
import { CartContext } from "./CartContext";

const categories = ["All", "New Arrivals", "Sneakers", "Loafers", "Cupsole", "Athletic", "T-Shirts", "Knitwear", "Denim", "Sale"];
const sortOptions = [
  ["featured", "Featured"],
  ["relevant", "Most relevant"],
  ["best", "Best selling"],
  ["az", "Alphabetically A–Z"],
  ["za", "Alphabetically Z–A"],
  ["price-low", "Price low–high"],
  ["price-high", "Price high–low"],
  ["newest", "Date new–old"],
];
const sizeOptions = ["35", "36", "37", "38", "39", "40", "41", "42", "43", "44", "45", "46", "XS", "S", "M", "L", "XL"];
const neverWishlisted = () => false;
const noop = () => {};

const collectionAliases = {
  bestsellers: ["bestseller", "popular"],
  "summer essentials": ["summer", "essential"],
  jackets: ["jacket"],
};

const matchesCollection = (product, collection) => {
  if (collection === "All") return true;
  if (collection === "Sale") return Boolean(product.compareAtPrice);

  const normalized = collection.toLowerCase();
  const terms = collectionAliases[normalized] || [
    normalized,
    normalized.replace(/s$/, ""),
  ];
  const searchable = [product.name, product.category, ...product.tags]
    .join(" ")
    .toLowerCase();

  return terms.some((term) => searchable.includes(term));
};

const keepFilterFocus = (event) => {
  if (event.key !== "Tab") return;
  const focusable = Array.from(
    event.currentTarget.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
    )
  ).filter((element) => element.offsetParent !== null);
  if (!focusable.length) return;

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
};

function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const cart = useContext(CartContext);
  const {
    isWishlisted = neverWishlisted,
    toggleWishlist = noop,
  } = cart;
  const [activeCategory, setActiveCategory] = useState("All");
  const [productType, setProductType] = useState("All");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("featured");
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [selectedFits, setSelectedFits] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [maxPrice, setMaxPrice] = useState(400);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [wishlistOnly, setWishlistOnly] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(12);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [brokenImages, setBrokenImages] = useState(new Set());
  const filterTriggerRef = useRef(null);
  const filterPanelRef = useRef(null);

  const colorOptions = useMemo(
    () => [...new Set(productData.flatMap((product) => product.colors))].sort(),
    []
  );

  useEffect(() => {
    const categoryParam = searchParams.get("category");
    const typeParam = searchParams.get("type");
    const viewParam = searchParams.get("view");
    const searchParam = searchParams.get("search");

    if (categoryParam) {
      const normalized = categoryParam.toLowerCase();
      const match = categories.find((category) => category.toLowerCase() === normalized);
      setActiveCategory(match || categoryParam);
    } else {
      setActiveCategory("All");
    }
    setProductType(typeParam === "Shoes" || typeParam === "Clothing" ? typeParam : "All");
    setWishlistOnly(viewParam === "wishlist");
    setQuery(searchParam || "");
  }, [searchParams]);

  useEffect(() => {
    setVisibleCount(12);
  }, [activeCategory, productType, query, sort, selectedSizes, selectedFits, selectedColors, maxPrice, inStockOnly, wishlistOnly]);

  useEffect(() => {
    if (!filtersOpen) return undefined;

    const focusTimer = window.setTimeout(() => {
      filterPanelRef.current?.querySelector("button")?.focus();
    }, 0);
    const closeOnEscape = (event) => {
      if (event.key !== "Escape") return;
      setFiltersOpen(false);
      filterTriggerRef.current?.focus();
    };
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [filtersOpen]);

  const filteredProducts = useMemo(() => {
    const rawQuery = query.trim().toLowerCase();
    const normalizedQuery = rawQuery === "new arrivals" ? "new" : rawQuery;
    const products = productData.filter((product) => {
      const categoryMatch = matchesCollection(product, activeCategory);
      const typeMatch = productType === "All" || product.productType === productType;
      const queryMatch =
        !normalizedQuery ||
        [product.name, product.category, product.material, ...product.tags, ...product.colors]
          .join(" ")
          .toLowerCase()
          .includes(normalizedQuery);
      const sizeMatch = !selectedSizes.length || selectedSizes.some((size) => product.size.includes(size));
      const fitMatch = !selectedFits.length || selectedFits.some((fit) => product.attributes.fit.toLowerCase().includes(fit.toLowerCase()));
      const colorMatch = !selectedColors.length || selectedColors.some((color) => product.colors.includes(color));
      const priceMatch = product.price <= maxPrice;
      const stockMatch = !inStockOnly || product.stock > 0;
      const wishlistMatch = !wishlistOnly || isWishlisted(product.id);
      return categoryMatch && typeMatch && queryMatch && sizeMatch && fitMatch && colorMatch && priceMatch && stockMatch && wishlistMatch;
    });

    return [...products].sort((a, b) => {
      if (sort === "best") return b.rating * b.reviews - a.rating * a.reviews;
      if (sort === "az") return a.name.localeCompare(b.name);
      if (sort === "za") return b.name.localeCompare(a.name);
      if (sort === "price-low") return a.price - b.price;
      if (sort === "price-high") return b.price - a.price;
      if (sort === "newest") return b.id - a.id;
      if (sort === "relevant") return Number(b.tags.includes("Popular")) - Number(a.tags.includes("Popular"));
      return Number(b.tags.includes("New")) - Number(a.tags.includes("New")) || a.id - b.id;
    });
  }, [activeCategory, inStockOnly, isWishlisted, maxPrice, productType, query, selectedColors, selectedFits, selectedSizes, sort, wishlistOnly]);

  const visibleProducts = filteredProducts.slice(0, visibleCount);
  const activeFilterCount = selectedSizes.length + selectedFits.length + selectedColors.length + Number(maxPrice < 400) + Number(inStockOnly) + Number(productType !== "All") + Number(wishlistOnly);

  const updateArrayFilter = (value, setter) => {
    setter((current) =>
      current.includes(value) ? current.filter((item) => item !== value) : [...current, value]
    );
  };

  const chooseCategory = (category) => {
    setActiveCategory(category);
    const next = new URLSearchParams(searchParams);
    if (category === "All") next.delete("category");
    else next.set("category", category);
    next.delete("view");
    setWishlistOnly(false);
    setSearchParams(next);
  };

  const updateSearchQuery = (value) => {
    setQuery(value);
    const next = new URLSearchParams(searchParams);
    if (value.trim()) next.set("search", value);
    else next.delete("search");
    setSearchParams(next, { replace: true });
  };

  const clearFilters = () => {
    setSelectedSizes([]);
    setSelectedFits([]);
    setSelectedColors([]);
    setMaxPrice(400);
    setInStockOnly(false);
    setProductType("All");
    setWishlistOnly(false);
    setQuery("");
    setActiveCategory("All");
    setSearchParams({});
  };

  const markLoaded = (id) => {
    setLoadedImages((current) => {
      const next = new Set(current);
      next.add(id);
      return next;
    });
  };

  const markBroken = (id) => {
    setBrokenImages((current) => {
      const next = new Set(current);
      next.add(id);
      return next;
    });
  };

  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const closeFilters = () => {
    setFiltersOpen(false);
    window.requestAnimationFrame(() => filterTriggerRef.current?.focus());
  };

  return (
    <main className="shop-page" id="main-content" tabIndex="-1">
      <header className="shop-hero">
        <p>{wishlistOnly ? "Saved selection" : productType === "All" ? "Footwear & apparel" : productType}</p>
        <h1>{wishlistOnly ? "Wishlist" : activeCategory === "All" ? "Shop all" : activeCategory}</h1>
        <span role="status" aria-live="polite">{filteredProducts.length} products</span>
      </header>

      <div className="shop-category-bar" aria-label="Product categories">
        {categories.map((category) => (
          <button
            type="button"
            key={category}
            className={activeCategory === category && !wishlistOnly ? "is-active" : ""}
            aria-pressed={activeCategory === category && !wishlistOnly}
            onClick={() => chooseCategory(category)}
          >
            {category}
          </button>
        ))}
      </div>

      <div className="shop-toolbar">
        <div className="shop-search">
          <label className="visually-hidden" htmlFor="collection-search">Search within products</label>
          <input id="collection-search" type="search" value={query} onChange={(event) => updateSearchQuery(event.target.value)} placeholder="Search products" />
          {query && <button type="button" onClick={() => updateSearchQuery("")} aria-label="Clear product search"><FiX /></button>}
        </div>
        <div className="shop-toolbar__actions">
          <button ref={filterTriggerRef} type="button" onClick={() => setFiltersOpen(true)}>
            <FiSliders aria-hidden="true" /> FILTERS {activeFilterCount > 0 && `(${activeFilterCount})`}
          </button>
          <label className="shop-sort">
            <span>SORT BY</span>
            <select value={sort} onChange={(event) => setSort(event.target.value)} aria-label="Sort products">
              {sortOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <FiChevronDown aria-hidden="true" />
          </label>
        </div>
      </div>

      {visibleProducts.length ? (
        <section className="shop-grid">
          {visibleProducts.map((product, index) => (
            <Fragment key={product.id}>
              {index === 6 && !wishlistOnly && (
                <Link className="shop-editorial-card" to="/about" onClick={scrollTop}>
                  <img src="/img/editorial/shop-craft.jpg" alt="An artisan hand-stitching an original leather loafer" loading="lazy" />
                  <span>Handcrafted in Portugal.<br />Built to last.</span>
                  <small>Discover our craft</small>
                </Link>
              )}
              <article className="shop-product-card">
                <Link className={`shop-product-card__visual ${loadedImages.has(product.id) ? "is-loaded" : ""}`} to={`/shopDetails/${product.id}`} onClick={scrollTop}>
                  {brokenImages.has(product.id) ? (
                    <div className="shop-product-card__fallback" role="img" aria-label={product.name}>
                      <span>{product.name}</span>
                    </div>
                  ) : (
                    <img
                      src={product.image}
                      alt={product.name}
                      loading="lazy"
                      onLoad={() => markLoaded(product.id)}
                      onError={() => markBroken(product.id)}
                    />
                  )}
                  {product.status && <span className={`shop-product-card__status ${product.compareAtPrice ? "is-sale" : ""}`}>{product.status}</span>}
                </Link>
                <button
                  className={`shop-product-card__wish ${isWishlisted(product.id) ? "is-active" : ""}`}
                  type="button"
                  onClick={() => toggleWishlist(product.id)}
                  aria-label={`${isWishlisted(product.id) ? "Remove" : "Add"} ${product.name} ${isWishlisted(product.id) ? "from" : "to"} wishlist`}
                >
                  <FiHeart aria-hidden="true" />
                </button>
                <Link className="shop-product-card__info" to={`/shopDetails/${product.id}`} onClick={scrollTop}>
                  <h2>{product.name}</h2>
                  <p>
                    {product.compareAtPrice && <s>{formatPrice(product.compareAtPrice)}</s>}
                    <span>{formatPrice(product.price)}</span>
                  </p>
                  <span>{product.colors.join(" / ")}</span>
                </Link>
              </article>
            </Fragment>
          ))}
        </section>
      ) : (
        <section className="shop-empty">
          <p>{wishlistOnly ? "Your wishlist is waiting for a first piece." : "No products match these filters."}</p>
          <button type="button" className="button-primary" onClick={clearFilters}>Clear filters</button>
        </section>
      )}

      {visibleCount < filteredProducts.length && (
        <div className="shop-load-more">
          <p>Showing {visibleProducts.length} of {filteredProducts.length}</p>
          <span><i style={{ width: `${(visibleProducts.length / filteredProducts.length) * 100}%` }} /></span>
          <button type="button" onClick={() => setVisibleCount((count) => count + 12)}>Show more results</button>
        </div>
      )}

      <div className={`shop-filter-overlay site-overlay ${filtersOpen ? "is-open" : ""}`} aria-hidden={!filtersOpen}>
        <button className="site-overlay__backdrop" type="button" aria-label="Close filters" onClick={closeFilters} />
        <aside ref={filterPanelRef} className="shop-filter-panel" role="dialog" aria-modal="true" aria-label="Product filters" onKeyDown={keepFilterFocus}>
          <div className="shop-filter-panel__header">
            <h2>Filters</h2>
            <button type="button" onClick={closeFilters} aria-label="Close filters"><FiX /></button>
          </div>

          <details open>
            <summary>Product type <FiChevronDown /></summary>
            <div className="filter-list">
              {["All", "Shoes", "Clothing"].map((type) => (
                <label key={type}><input type="radio" name="product-type" checked={productType === type} onChange={() => setProductType(type)} /> <span>{type}</span></label>
              ))}
            </div>
          </details>

          <details>
            <summary>Size <FiChevronDown /></summary>
            <div className="filter-size-grid">
              {sizeOptions.map((size) => (
                <label key={size}><input type="checkbox" checked={selectedSizes.includes(size)} onChange={() => updateArrayFilter(size, setSelectedSizes)} /><span>{size}</span></label>
              ))}
            </div>
          </details>

          <details>
            <summary>Fit <FiChevronDown /></summary>
            <div className="filter-list">
              {["Slim", "Regular", "Relaxed"].map((fit) => (
                <label key={fit}><input type="checkbox" checked={selectedFits.includes(fit)} onChange={() => updateArrayFilter(fit, setSelectedFits)} /><span>{fit}</span></label>
              ))}
            </div>
          </details>

          <details>
            <summary>Color <FiChevronDown /></summary>
            <div className="filter-list filter-list--colors">
              {colorOptions.map((color) => (
                <label key={color}><input type="checkbox" checked={selectedColors.includes(color)} onChange={() => updateArrayFilter(color, setSelectedColors)} /><span>{color}</span></label>
              ))}
            </div>
          </details>

          <details>
            <summary>Price <FiChevronDown /></summary>
            <div className="filter-range">
              <input type="range" min="35" max="400" step="5" value={maxPrice} onChange={(event) => setMaxPrice(Number(event.target.value))} aria-label="Maximum product price" />
              <span>Up to {formatPrice(maxPrice)}</span>
            </div>
          </details>

          <details>
            <summary>Availability <FiChevronDown /></summary>
            <div className="filter-list">
              <label><input type="checkbox" checked={inStockOnly} onChange={(event) => setInStockOnly(event.target.checked)} /><span>In stock only</span></label>
              <label><input type="checkbox" checked={wishlistOnly} onChange={(event) => setWishlistOnly(event.target.checked)} /><span>Wishlist only</span></label>
            </div>
          </details>

          <div className="shop-filter-panel__footer">
            <button type="button" onClick={clearFilters}>Clear filters</button>
            <button type="button" onClick={closeFilters}>Show {filteredProducts.length} products</button>
          </div>
        </aside>
      </div>
    </main>
  );
}

export default Shop;
